import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarIcon, Info } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '../hooks/use-toast';
import { createReservation, getBlockedSlots } from '../services/api';
import { TIME_SLOTS, isTimeBlocked, blockLabel, capitalizeFirst } from '../lib/timeSlots';

const Reservations = () => {
  const { toast } = useToast();
  const [date, setDate] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    guests: '',
    time: '',
    specialRequests: ''
  });

  const [blocks, setBlocks] = useState([]);

  const loadBlocks = () => getBlockedSlots().then(setBlocks).catch(() => {});
  useEffect(() => { loadBlocks(); }, []);

  const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
  const dayBlocks = blocks.filter((b) => b.date === dateStr);
  const closedDays = blocks.filter((b) => b.all_day);
  const closedSet = new Set(closedDays.map((b) => b.date));

  const handleDateSelect = (d) => {
    setDate(d);
    const ds = d ? format(d, 'yyyy-MM-dd') : '';
    if (formData.time && isTimeBlocked(blocks.filter((b) => b.date === ds), formData.time)) {
      setFormData((prev) => ({ ...prev, time: '' }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !formData.name || !formData.email || !formData.phone || !formData.guests || !formData.time) {
      toast({
        title: "Información Faltante",
        description: "Por favor complete todos los campos requeridos.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createReservation({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: format(date, 'yyyy-MM-dd'),
        time: formData.time,
        guests: parseInt(formData.guests),
        special_requests: formData.specialRequests
      });

      toast({
        title: "¡Reservación Enviada!",
        description: `¡Gracias ${formData.name}! Su reservación ha sido recibida.`
      });

      setSubmittedData({
        name: formData.name,
        date: format(date, 'PPP', { locale: es }),
        time: formData.time,
        guests: formData.guests
      });
      setSubmitted(true);

      setFormData({
        name: '',
        email: '',
        phone: '',
        guests: '',
        time: '',
        specialRequests: ''
      });
      setDate(undefined);
    } catch (error) {
      console.error('Error creating reservation:', error);
      if (error.status === 409) loadBlocks();
      toast({
        title: "Error",
        description: error.status === 409 ? error.message : "No se pudo enviar la reservación. Intenta de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reservations" className="py-24 px-4" style={{ backgroundColor: '#FFCCE0' }}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2
            className="md:text-6xl !font-bold !text-5xl mb-4"
            style={{ fontFamily: "'Crimson Text', serif" }}>Reserva ahora


          </h2>
          <p className="max-w-2xl !text-lg mx-auto text-gray-700">Reserva tu mesa y disfruta de los sabores auténticos de Francia.

          </p>
        </div>

        {submitted ? (
          <Card className="bg-white/90 backdrop-blur-sm shadow-lg max-w-xl mx-auto" data-testid="reservation-success-message">
            <CardContent className="p-10 text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#FFCCE0' }}>
                <CalendarIcon className="w-8 h-8 text-gray-700" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: "'Crimson Text', serif" }}>
                ¡Reservación Enviada!
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Gracias <strong>{submittedData?.name}</strong>, su solicitud de reservación ha sido enviada exitosamente.
                Le confirmaremos por correo electrónico a la brevedad.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm text-left mx-auto inline-block">
                <p className="text-gray-500 mb-1">Fecha: <strong className="text-gray-800">{submittedData?.date}</strong></p>
                <p className="text-gray-500 mb-1">Hora: <strong className="text-gray-800">{submittedData?.time}</strong></p>
                <p className="text-gray-500">Personas: <strong className="text-gray-800">{submittedData?.guests}</strong></p>
              </div>
              <br />
              <Button
                onClick={() => setSubmitted(false)}
                className="mt-2 bg-black hover:bg-gray-800 text-white"
                data-testid="new-reservation-btn"
              >
                Hacer otra reservación
              </Button>
            </CardContent>
          </Card>
        ) : (
        <Card className="bg-white/90 backdrop-blur-sm shadow-lg">
          <CardHeader>
            <CardTitle className="!font-semibold !text-2xl">Reserva tu mesa</CardTitle>
            <CardDescription className="!text-sm text-muted-foreground">Rellena los siguientes datos y le confirmaremos su reservacion</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Nombre completo *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Juan Pérez"
                    required />

                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="juan@ejemplo.com"
                    required />

                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Numero de contacto *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    required />

                </div>
                <div className="space-y-2">
                  <Label htmlFor="guests" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Número de invitados *</Label>
                  <Select
                    name="guests"
                    value={formData.guests}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, guests: value }))}>

                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar invitados" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) =>
                      <SelectItem key={num} value={num.toString()}>
                          {num} {num === 1 ? 'Invitado' : 'Invitados'}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Fecha *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal">

                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, 'PPP', { locale: es }) : 'Elegir una fecha'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        locale={es}
                        selected={date}
                        onSelect={handleDateSelect}
                        disabled={(d) => d < new Date() || closedSet.has(format(d, 'yyyy-MM-dd'))}
                        initialFocus />

                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Hora *</Label>
                  <Select
                    name="time"
                    value={formData.time}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, time: value }))}>

                    <SelectTrigger data-testid="reservation-time-select">
                      <SelectValue placeholder="Seleccionar hora" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((value) => {
                        const blocked = isTimeBlocked(dayBlocks, value);
                        return (
                          <SelectItem key={value} value={value} disabled={blocked} data-testid={`time-option-${value}`}>
                            {value}{blocked ? ' · No disponible' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {dayBlocks.length > 0 && (
                <div className="flex gap-3 rounded-lg border border-pink-200 bg-pink-50 p-4 text-sm text-gray-700" data-testid="reservation-day-block-notice">
                  <Info className="w-5 h-5 shrink-0 text-pink-500" />
                  <div className="space-y-1">
                    {dayBlocks.map((b) => (
                      <p key={b.id}><strong>No disponible {blockLabel(b).toLowerCase()}</strong>{b.reason ? ` — ${b.reason}` : ''}</p>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="specialRequests" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Solicitudes especiales (opcional)</Label>
                <Input
                  id="specialRequests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  placeholder="Restricciones dietéticas, ocasión, preferencias..." />

              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 w-full hover:bg-gray-900 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg !font-medium !text-lg gap-2 px-4 py-6 rounded-md shadow text-white bg-black">
                {isSubmitting ? 'Enviando...' : 'Confirmar Reservación'}
              </Button>

              {closedDays.length > 0 && (
                <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600" data-testid="reservation-closed-days">
                  <p className="font-semibold text-gray-800 mb-2">Días sin reservaciones</p>
                  <ul className="space-y-1">
                    {closedDays.slice(0, 6).map((b) => (
                      <li key={b.id} data-testid={`closed-day-${b.date}`}>
                        <span>{capitalizeFirst(format(parseISO(b.date), "EEEE d 'de' MMMM yyyy", { locale: es }))}</span>
                        {b.reason ? ` — ${b.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
        )}
      </div>
    </section>);

};

export default Reservations;